import { EmbeddedDataService } from '@allianz/ngx-aem-integration';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NxDateAdapter } from '@aposin/ng-aquila/datefield';
import { NxDropdownSelectChange } from '@aposin/ng-aquila/dropdown';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Moment } from 'moment';
import { Subject, filter, first, interval, switchMap, takeUntil, tap } from 'rxjs';
import packageJSON from './../../package.json';
import { AemConfiguration } from './config/aem-configuration.type';
import { environment } from './environments/environment';
import { AppType } from './model/app-type.model';
import { ApiService } from './services/api.service';
import { FormsService } from './services/forms.service';
import { PersistStorageKeys, PersistStorageService } from './services/persist-storage.service';
import { ProcessService } from './services/process.service';
import { AppState } from './store';
import * as fromConfig from './store/config/config.reducer';
import * as fromInsuredImport from './store/insured-import/insured-import.reducer';
import * as fromProcess from './store/process/process.reducer';
import { TrackingService } from './tracking/tracking.service';
import isAppAgentOrTravelAgency from './utils/is-agent-or-travel-agency.util';
import { SessionStorageService } from './services/session-storage.service';

const IS_LOGGED_IN_POOLING_INTERVAL = 600000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'az-travel';

  debugMode = false;
  debugModeVisible = false;
  debugAppKind = environment.app;
  debugVersion = (packageJSON as { version: string }).version;
  debugBuild = (packageJSON as { build: string }).build;
  isAppAgent = environment.app === AppType.Agent;
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();

  isProd = environment.production;
  hotjarId = environment.hotjarId;

  currentStep$ = this.store.select(fromProcess.selectProcessStep);
  ngDestroy$ = new Subject<void>();

  currentLanguage = this.translateService.currentLang;
  configIsLoading$ = this.store.select(fromConfig.selectConfigIsLoading);
  isSuccess$ = this.store.select(fromProcess.selectProcessIsSuccess);
  discountCodeIsValid$ = this.store.select(fromProcess.selectOfferDiscountCodeIsValid);
  importedToForm$ = this.store.select(fromInsuredImport.selectInsuredImportedToForm);
  procesStep = this.store.selectSignal(fromProcess.selectProcessStep);
  languageList = [
    { code: 'en', name: 'English' },
    { code: 'pl', name: 'polski' },
  ];

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private formsService: FormsService,
    private persistStorage: PersistStorageService,
    private processService: ProcessService,
    private translateService: TranslateService,
    private embeddedDataService: EmbeddedDataService<{ foo: 'bar' }, AemConfiguration>,
    readonly nxDateAdapter: NxDateAdapter<Moment>,
    private trackingService: TrackingService,
    private apiService: ApiService,
    private sessionStorageService: SessionStorageService,
  ) {
    this.title = embeddedDataService.CustomConfiguration?.applicationId || 'az-travel';

    this.debugMode = environment.debug;
  }

  ngOnInit(): void {
    this.authRefreshPooling();
    this.checkClearPersistStore();
    this.saveDiscountCode();
    this.saveAffiliateNumber();
    this.savePath();
    this.store.dispatch(fromConfig.actions.fetchStart());
    if (isAppAgentOrTravelAgency()) {
      this.store.dispatch(fromConfig.actions.fetchMenuConfigStart());
      this.store.dispatch(fromConfig.actions.fetchGlobtroterUserStart());
      this.store.dispatch(fromConfig.actions.fetchGlobtroterConfigStart());
    }
    this.formsService.ngOnInit();
    this.persistStorage.onAppInit();
    this.setDatePickerLocale();
    this.closeInsuredImportedOnStepChange();
    this.implementTracking();
    this.addHotjarScript();
  }

  ngOnDestroy(): void {
    this.formsService.ngOnDestroy();
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  handleChangeLanguage(language: NxDropdownSelectChange<string>): void {
    void this.translateService.use(language.value);
  }

  handleDevClearStore(): void {
    localStorage.clear();
    location.reload();
  }

  turnOffDebugMode(): void {
    this.debugMode = false;
  }

  turnOnTrackingDebugMode(): void {
    this.persistStorage.setBoolean(PersistStorageKeys.TrackingDebug, true);
  }

  turnOffTrackingDebugMode(): void {
    this.persistStorage.setBoolean(PersistStorageKeys.TrackingDebug, false);
  }

  toggleDebugModeVisibility(): void {
    if (!this.debugMode) {
      this.debugModeVisible = false;
    }

    this.debugModeVisible = !this.debugModeVisible;
  }

  closeInsuredImported() {
    this.store.dispatch(fromInsuredImport.actions.succesfullyImported({ importedToForm: false }));
  }

  closeInsuredImportedOnStepChange() {
    this.currentStep$.pipe(takeUntil(this.ngDestroy$)).subscribe(() => {
      this.store.dispatch(fromInsuredImport.actions.succesfullyImported({ importedToForm: false }));
    });
  }

  private implementTracking(): void {
    this.translateService
      .get('language')
      .pipe(first())
      .subscribe((language: string) => {
        this.trackingService.init({
          pageName: document.title,
          language,
          siteSection: 'az-travel', // @TODO: Not sure if this is correct
        });
      });

    this.trackingService.setUserType(environment.app === AppType.Direct ? 'customer' : 'agent');

    this.currentStep$.pipe(takeUntil(this.ngDestroy$)).subscribe((step) => {
      this.trackingService.setStep(step);
    });
  }

  private setDatePickerLocale(): void {
    this.translateService
      .get('nx-datepicker.locale')
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((locale: string) => {
        this.nxDateAdapter.setLocale(locale);
      });
  }

  private checkClearPersistStore(): void {
    const isSuccessPage = location.href.includes('/success');

    this.isSuccess$.pipe(first()).subscribe((isSuccess) => {
      if (!isSuccessPage && isSuccess) {
        this.persistStorage.removeAll();
        this.store.dispatch(fromProcess.actions.setSuccess({ success: false }));
        void this.router.navigate(['page', 'step1']);
      }
    });
  }

  private saveDiscountCode(): void {
    const url = new URL(window.location.href);
    const discountCode = url.searchParams.get('discountcode');

    if (discountCode) {
      this.persistStorage.setString(PersistStorageKeys.DiscountCode, discountCode, 'session');
    }
  }

  private saveAffiliateNumber(): void {
    const url = new URL(window.location.href);
    const affiliateNumber = url.searchParams.get('affiliateNumber');

    if (affiliateNumber) {
      this.persistStorage.setString(PersistStorageKeys.AffiliateNumber, affiliateNumber, 'session');
    }
  }

  private savePath(): void {
    const url = new URL(window.location.href);
    const path = url.searchParams.get('path');

    if (path) {
      this.persistStorage.setString(PersistStorageKeys.Path, path, 'session');
    }
  }

  private addHotjarScript() {
    if (!this.isProd || !this.hotjarId) {
      return;
    }
    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.text = `(function(h,o,t,j,a,r){
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:${this.hotjarId},hjsv:6};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;

    if (head) {
      head.appendChild(script);
    }
  }

  private authRefreshPooling() {
    interval(IS_LOGGED_IN_POOLING_INTERVAL)
      .pipe(
        takeUntil(this.ngDestroy$),
        filter(() => environment.app !== AppType.Direct),
        switchMap(() => this.apiService.getSSOAuthCheck()),
        tap((response) => !response.logged && void this.router.navigate(['/error/auth'])),
      )
      .subscribe();
  }
}
